import React from 'react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { AlertTriangle, Copy, FileText, X } from 'lucide-react';
import { t } from '../../i18n';

const ConflictModal: React.FC = () => {
  const { conflictState, resolveConflict } = useFileManagerStore();

  if (!conflictState) return null;

  const { sourceFile, targetPath } = conflictState;
  const fileName = targetPath.split('/').pop();

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[#202020] w-[500px] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#2d2d2d] px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-yellow-500">
            <AlertTriangle size={20} />
            <span className="font-medium text-slate-200">{t('conflictTitle')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 mb-4">
            {t('conflictMessage', { name: fileName || '' })}
          </p>
          
          <div className="flex items-center justify-between bg-white/5 p-3 rounded mb-6">
             <div className="flex items-center space-x-3">
                 <FileText size={32} className="text-blue-400" />
                 <div>
                     <div className="text-sm font-medium text-slate-200">{fileName}</div>
                     <div className="text-xs text-slate-500">{t('conflictExisting')}</div>
                 </div>
             </div>
          </div>

          <p className="text-xs text-slate-500 mb-4">{t('conflictQuestion')}</p>

          <div className="space-y-2">
             <button 
                onClick={() => resolveConflict('overwrite', false)}
                className="w-full flex items-center px-4 py-3 bg-white/5 hover:bg-white/10 rounded transition-colors text-left group"
             >
                <Copy size={20} className="mr-3 text-red-400 group-hover:text-red-300" />
                <div>
                    <div className="text-sm font-medium text-slate-200">{t('conflictReplace')}</div>
                    <div className="text-xs text-slate-500">{t('conflictReplaceDesc')}</div>
                </div>
             </button>

             <button 
                onClick={() => resolveConflict('rename', false)}
                className="w-full flex items-center px-4 py-3 bg-white/5 hover:bg-white/10 rounded transition-colors text-left group"
             >
                <FileText size={20} className="mr-3 text-win-accent group-hover:text-blue-300" />
                <div>
                    <div className="text-sm font-medium text-slate-200">{t('conflictRename')}</div>
                    <div className="text-xs text-slate-500">{t('conflictRenameDesc')}</div>
                </div>
             </button>

             <button 
                onClick={() => resolveConflict('skip', false)}
                className="w-full flex items-center px-4 py-3 bg-white/5 hover:bg-white/10 rounded transition-colors text-left group"
             >
                <X size={20} className="mr-3 text-slate-400 group-hover:text-slate-300" />
                <div>
                    <div className="text-sm font-medium text-slate-200">{t('conflictSkip')}</div>
                    <div className="text-xs text-slate-500">{t('conflictSkipDesc')}</div>
                </div>
             </button>
          </div>
          
          <div className="mt-6 pt-4 border-t border-white/5">
              <p className="text-xs text-slate-500 mb-2">{t('conflictApplyAll')}</p>
              <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => resolveConflict('overwrite', true)} className="text-xs bg-red-500/20 text-red-300 py-1.5 rounded hover:bg-red-500/30">{t('conflictOverwriteAll')}</button>
                  <button onClick={() => resolveConflict('rename', true)} className="text-xs bg-blue-500/20 text-blue-300 py-1.5 rounded hover:bg-blue-500/30">{t('conflictRenameAll')}</button>
                  <button onClick={() => resolveConflict('skip', true)} className="text-xs bg-slate-500/20 text-slate-300 py-1.5 rounded hover:bg-slate-500/30">{t('conflictSkipAll')}</button>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictModal;
